import Box from '@mui/material/Box';
import { TabProps } from '@mui/material/Tab';

import React, { FC, ReactNode, useId } from 'react';

import { Link } from 'react-router-dom';

import MuiIcon from '../MuiIcon';

import { MuiIconName } from '../MuiIcon/types';

import * as Styled from './styled';

export type TabEntry = {
  content: ReactNode;
  icon: MuiIconName;
  label: TabProps['label'];
  url: string;
};

type Props = {
  selectedTab: number;
  tabs: TabEntry[];
};

/**
 * Tabs component with rounded tabs
 *
 * @param props             Props passed to the component
 * @param props.selectedTab Index of the selected tab
 * @param props.tabs        Tabs to render (with label, icon and content)
 * @returns                 Component itself
 */
const Tabs: FC<Props> = ({ selectedTab, tabs }) => {
  const tabId = useId();
  const panelId = useId();

  return (
    <Box>
      <Styled.StyledTabs value={selectedTab}>
        {tabs.map(({ icon, label, url }, index) => {
          const isSelected = selectedTab === index;
          const tabIcon = <MuiIcon fontSize="small" icon={icon} />;
          const id = `${tabId}-${index}`;

          const dynamicProps: TabProps = isSelected
            ? { icon: tabIcon, label, tabIndex: -1 }
            : {
                label: (
                  <Styled.StyledChip
                    icon={tabIcon}
                    label={label}
                    variant="outlined"
                  />
                ),
                tabIndex: 0,
              };

          return (
            <Styled.StyledTab
              LinkComponent={Link}
              aria-controls={`${panelId}-${index}`}
              data-is-first={index === 0}
              disableRipple={true}
              iconPosition="start"
              id={id}
              key={id}
              to={url}
              {...dynamicProps}
            />
          );
        })}
      </Styled.StyledTabs>
      {tabs.map(({ content }, index) => {
        const shouldShow = selectedTab === index;
        const id = `${panelId}-${index}`;

        return (
          <Styled.StyledPanel
            aria-labelledby={`${tabId}-${index}`}
            data-is-first-selected={index === 0}
            hidden={!shouldShow}
            id={id}
            key={id}
            role="tabpanel"
          >
            {shouldShow ? content : null}
          </Styled.StyledPanel>
        );
      })}
    </Box>
  );
};

export default Tabs;
